import React, {useEffect, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import {Checkbox, FormControlLabel, TextField, Box, InputAdornment} from '@mui/material';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {cleanEmailList, parseEmailList} from '../../../utils/email';
import {CheckableSupplierEmails} from './CheckableSupplierEmails.jsx';
import {ErrorFlow, BrickContext, Config} from 'olympe';
import CircularProgress from '@mui/material/CircularProgress';
import {UserFieldMultiSelect} from './UserFieldMultiSelect.jsx';
import {
    companyCodesEnablePOSendingOptions, LAMBDA_NAMES,
    SOURCES,
} from '../../../utils/constants.js';
import {getP2POutputTableLambda} from '../../../utils/functionSignatures';
import {useFieldEditability} from '../utils/useFieldEditability.js';

/**
 * @param {SendingOptionsDetails.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function SendingOptionsDetails(props) {
    const useRequestStore = requestStores.get(props.prTag);

    // Form state
    const [sendToRequester, setSendToRequester] = useRequestStore((state) => [state.sendToRequester, state.setSendToRequester]);
    const [sendToSpender, setSendToSpender] = useRequestStore((state) => [state.sendToSpender, state.setSendToSpender]);
    const [sendToOther, setSendToOther] = useRequestStore((state) => [state.sendToOther, state.setSendToOther]);
    const [otherAddress, setOtherAddress] = useRequestStore((state) => [state.otherAddress, state.setOtherAddress]);
    const [sendToSupplier, setSendToSupplier] = useRequestStore((state) => [state.sendToSupplier, state.setSendToSupplier]);
    const [supplierAddresses, setSupplierAddresses] = useRequestStore((state) => [state.supplierAddresses, state.setSupplierAddresses]);
    const [p2pSupplier, setP2pSupplier] = useRequestStore((state) => [state.p2pSupplier, state.setP2pSupplier]);
    const [p2pOther, setP2pOther] = useRequestStore((state) => [state.p2pOther, state.setP2pOther]);
    const [vmdSupplier, setVmdSupplier] = useRequestStore((state) => [state.vmdSupplier, state.setVmdSupplier]);
    const [pslSupplier, setPslSupplier] = useRequestStore((state) => [state.pslSupplier, state.setPslSupplier]);
    const vendorContactEmail = useRequestStore((state) => state.vendorContactEmails);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const [hidePrice, setHidePrice] = useRequestStore((state) => [state.hidePrice, state.setHidePrice]);
    const [externalComment, setExternalComment] = useRequestStore((state) => [state.externalComment, state.setExternalComment]);
    const source = useRequestStore((state) => state.source);
    // Lambdas state + executions
    const [defaultValuesAfterLoading, setDefaultValuesAfterLoading] = useState(false);


    const getP2POutputData = props.context.get(LAMBDA_NAMES.GET_P2P_OUTPUT_DATA);
    const [companyCode, purchaseOrganization, purchasingGroup, plant, vendorCode] = useRequestStore((state) => [state.companyCode, state.purchaseOrganization, state.purchasingGroup, state.defaultPlant, state.vendorCode]);
    const [p2pSupplierAddresses, setP2pSupplierAddresses] = useRequestStore((state) => [state.p2pSupplierAddresses, state.setP2pSupplierAddresses]);
    const [p2pOtherSupplierAddresses, setP2pOtherSupplierAddresses] = useRequestStore((state) => [state.p2pOtherSupplierAddresses, state.setP2pOtherSupplierAddresses]);
    const [coversheet, setCoversheet] = useRequestStore((state) => [state.coversheet, state.setCoversheet]);
    const [loadingCoversheet, setLoadingCoversheet] = useState(false);
    const [loadingP2p, setLoadingP2p] = useState(false);
    const [loadingP2pOther, setLoadingP2pOther] = useState(false);
    const [showErrorTooltipP2P, setShowErrorTooltipP2P] = useState(false);
    const [showErrorTooltipP2POther, setShowErrorTooltipP2POther] = useState(false);
    const [showErrorTooltipVMD, setShowErrorTooltipVMD] = useState(false);
    const [errorP2P, setErrorP2P] = useState({code: 0, body: ''});
    const [errorP2POther, setErrorP2POther] = useState({code: 0, body: ''});
    const [errorVMD, setErrorVMD] = useState({code: 0, body: ''});

    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});
    const disableCandex = source === SOURCES.CANDEX;
    const hidePriceDisabled = isDisabled('sendingOptions.hidePrice');

    const userHasAccessToPOSendingOptions = useMemo(() => {
        const env = Config.getParameter('ENV');

        return companyCodesEnablePOSendingOptions[env].includes(companyCode.value);
    }, [companyCode.value]);

    /**
     * @param {string} value
     * @return {boolean} True if all the email addresses in the string are valid, false otherwise
     */

    // This useEffect will fetch the P2P output table data with the vendor filter (in Supplier section)
    useEffect(() => {
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;
        // eslint-disable-next-line require-jsdoc
        async function doEffect() {
            setLoadingP2p(true);
            setLoadingCoversheet(true);

            getP2POutputTableLambda(props.context,
                getP2POutputData,
                companyCode.value,
                purchaseOrganization.value,
                purchasingGroup.value,
                plant.value,
                vendorCode.toString().padStart(10, '0'),
                userIdToCallSAPAPIs,
                (result) => {
                    if (!cleanupFunction) {
                        let emailList;
                        try {
                            const data = JSON.parse(result);

                            const coversheets = data.map((o) => o['Coversheet']).filter((e) => !!e);
                            setCoversheet([...coversheets].join(','));
                            setLoadingCoversheet(false);

                            // No need to go beyond if the supplier box is not checked
                            if (!sendToSupplier && !sendToOther) {
                                setLoadingP2p(false);
                                return;
                            }

                            emailList = data
                                .filter((e) => e['Supplier'] === 'X')
                                .map((o) => o['VndrEmail']);

                            emailList = cleanEmailList(emailList);
                        } catch (e) {
                            console.error(e);
                            setShowErrorTooltipP2P(true);
                        }
                        if (emailList !== undefined) {
                            setP2pSupplierAddresses(emailList);
                            // Force apply the default supplier now that the VMD addresses have been received
                            if (defaultValuesAfterLoading) {
                                setSendToSupplier(sendToSupplier);
                            }
                        }
                        setLoadingP2p(false);
                        // reset default values loading flag if both P2P and VMD have been loaded
                        if (defaultValuesAfterLoading && !loadingVmd) {
                            setDefaultValuesAfterLoading(false);
                        }
                    }
                },
                (reason) => {
                    console.error(reason);
                    setErrorP2P({code: reason.getCode(), body: reason.getMessage()});
                    setShowErrorTooltipP2P(true);
                    setLoadingP2p(false);
                    setLoadingCoversheet(false);
                },
            );
        }
        doEffect();
        return () => cleanupFunction = true;
    }, [sendToSupplier, companyCode, purchaseOrganization, plant, purchasingGroup]);

    // This useEffect will fetch the P2P output table data without the vendor filter (in Other section)
    useEffect(() => {
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;
        // eslint-disable-next-line require-jsdoc
        async function doEffect() {
            // No need to do anything if the "Other" box is not checked
            if (!sendToOther) {
                return;
            }
            setLoadingP2pOther(true);

            getP2POutputTableLambda(props.context,
                getP2POutputData,
                companyCode.value,
                purchaseOrganization.value,
                purchasingGroup.value,
                plant.value,
                '',
                userIdToCallSAPAPIs,
                (result) => {
                    if (!cleanupFunction) {
                        let emailList;
                        try {
                            const data = JSON.parse(result);

                            emailList = data
                                .filter((e) => e['Supplier'] === '')
                                .map((o) => o['VndrEmail']);

                            emailList = cleanEmailList(emailList);
                        } catch (e) {
                            console.error(e);
                            setShowErrorTooltipP2POther(true);
                        }
                        if (emailList !== undefined) {
                            setP2pOtherSupplierAddresses(emailList);
                        }
                        setLoadingP2pOther(false);
                        // reset default values loading flag if both P2P and VMD have been loaded
                        if (defaultValuesAfterLoading && !loadingVmd) {
                            setDefaultValuesAfterLoading(false);
                        }
                    }
                },
                (reason) => {
                    console.error(reason);
                    setErrorP2POther({code: reason.getCode(), body: reason.getMessage()});
                    setShowErrorTooltipP2POther(true);
                    setLoadingP2pOther(false);
                },
            );
        }
        doEffect();
        return () => cleanupFunction = true;
    }, [sendToOther, companyCode, purchaseOrganization, plant, purchasingGroup]);

    const getVendorEmails = props.context.get(LAMBDA_NAMES.GET_VENDOR_EMAILS);
    const [vmdSupplierAddresses, setVmdSupplierAddresses] = useRequestStore((state) => [state.vmdSupplierAddresses, state.setVmdSupplierAddresses]);
    const [loadingVmd, setLoadingVmd] = useState(false);

    // This useEffect will fetch the vendor emails
    useEffect(() => {
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;
        // compute only if Supplier addresses is checked
        if (!sendToSupplier) {
            return;
        }
        // eslint-disable-next-line require-jsdoc
        async function doEffect() {
            setLoadingVmd(true);
            const [controlFlowIn, vendorCodeIn, requesterIdIn] = getVendorEmails.getInputs();
            const [controlFlowOut, errorFlowOut, dataOut] = getVendorEmails.getOutputs();
            const runner$ = props.context.runner(getVendorEmails)
                .set(vendorCodeIn, vendorCode)
                .set(requesterIdIn, userIdToCallSAPAPIs)
                .trigger(controlFlowIn);

            const outputFlow = await Promise.any([
                runner$.waitFor(controlFlowOut),
                runner$.waitFor(errorFlowOut),
            ]);
            if (outputFlow instanceof ErrorFlow) {
                console.error(outputFlow);
                setErrorVMD({code: outputFlow.getCode(), body: runner$.get(dataOut)});
                setShowErrorTooltipVMD(true);
                setLoadingVmd(false);
            } else {
                if (!cleanupFunction) {
                    let emailList;
                    try {
                        const data = JSON.parse(runner$.get(dataOut));
                        emailList = data.map((o) => o['EmailAddr']);
                        emailList = cleanEmailList(emailList);
                    } catch (e) {
                        console.error(e);
                        setShowErrorTooltipVMD(true);
                    }
                    if (emailList !== undefined) {
                        setVmdSupplierAddresses(emailList);
                        // Force apply the default supplier now that the VMD addresses have been received
                        if (defaultValuesAfterLoading) {
                            setSendToSupplier(sendToSupplier);
                        }
                    }
                    setLoadingVmd(false);
                    // reset default values loading flag if both P2P and VMD have been loaded
                    if (defaultValuesAfterLoading && !loadingP2p) {
                        setDefaultValuesAfterLoading(false);
                    }
                }
            }
            runner$.destroy();
        }
        doEffect();
        return () => cleanupFunction = true;
    }, [sendToSupplier, vendorCode]);

    useEffect(() => {
        if (!userHasAccessToPOSendingOptions && (sendToRequester || sendToSpender || sendToSupplier || sendToOther || otherAddress)) {
            setSendToRequester(false);
            setSendToSpender(false);
            setSendToSupplier(false);
            setSendToOther(false);
            setP2pOther(false);
            setOtherAddress('');
        }
    }, [userHasAccessToPOSendingOptions, sendToRequester, sendToSpender, sendToSupplier, sendToOther, otherAddress, setP2pOther]);

    // useEffect used to keep at least the requester checked if everything else is unchecked
    useEffect(() => {
        if (userHasAccessToPOSendingOptions && !sendToRequester && !sendToSpender && !sendToSupplier && !sendToOther && ((!hidePriceDisabled && !hidePrice) || hidePriceDisabled)) {
            setSendToRequester(true);
        }
    }, [userHasAccessToPOSendingOptions, sendToRequester, sendToSpender, sendToSupplier, sendToOther, setP2pOther, hidePrice, hidePriceDisabled]);

    // This useEffect set the email of the spender and the requester in the internal comment when the PR is open for the
    // first time (as we have spender a requester enabled by default)
    useEffect(() => {
        if (sendToRequester && sendToSpender) {
            setSendToSpender(true);
        }
    }, [sendToRequester, sendToSpender]);

    const handleSendToSupplierChange = (value) => {
        setSendToSupplier(value);
        // The user explicitly clicked on the checkbox => when P2P and VMD results arrive, apply default addresses
        if (value) {
            setDefaultValuesAfterLoading(true);
        }
    };

    const handleSupplierAddressesChange = (newVal) => {
        setSupplierAddresses(parseEmailList(newVal));
    };

    const handleP2pSupplierChange = setP2pSupplier;
    const handleP2pOtherChange = setP2pOther;

    const handleVmdSupplierChange = setVmdSupplier;

    const handlePslSupplierChange = setPslSupplier;


    return (
        <Box className={'pr-editor-centered-submodule'}>
            <Typography variant="h5" style={{marginTop: '40px', color: '#CCCCCC'}}>PO sending options</Typography>
            {!userHasAccessToPOSendingOptions && !isDisabled('sendingOptions.sendToRequester') && (
                <p style={{color: 'red', fontSize: '14px'}}>PO PDF Recipients are not available for selection. You cannot benefit from the automatic sending of the PO PDF to a list of people you would define here as the company code you use is not configured rightly. Please contact your market PPA team to activate it.</p>
            )}
            <p>PO PDF recipients: </p>
            <Box sx={{display: 'flex', alignItems: 'center'}} gap={1}>
                <FormControlLabel control={
                    <Checkbox
                        disabled={isDisabled('sendingOptions.sendToRequester') ||
                            !userHasAccessToPOSendingOptions}
                        checked={sendToRequester}
                        onChange={(event) => setSendToRequester(event.target.checked)} />
                } label="Requester"/>
                <FormControlLabel control={
                    <Checkbox
                        disabled={isDisabled('sendingOptions.sendToSpender') ||
                            !userHasAccessToPOSendingOptions}
                        checked={sendToSpender}
                        onChange={(event) => setSendToSpender(event.target.checked)} />
                } label="Spender"/>
            </Box>
            <CheckableSupplierEmails
                label="Supplier"
                checked={sendToSupplier}
                loading={loadingP2p || loadingVmd}
                emails={supplierAddresses}
                onChecked={handleSendToSupplierChange}
                onTextChange={handleSupplierAddressesChange}
                disableTooltip
                disableText={!sendToSupplier || defaultValuesAfterLoading || isDisabled('sendingOptions.supplierEmails') || !userHasAccessToPOSendingOptions || disableCandex}
                controlLabelWidth="200px"
                disableCheckbox={isDisabled('sendingOptions.supplier') || !userHasAccessToPOSendingOptions || disableCandex}
            />
            {sendToSupplier && <Box sx={{display: 'flex', alignItems: 'top'}} gap={5} className="pr-editor-sending-details-supplier-from-container">
                <p>Use emails from: </p>
                <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 2}}>
                    <CheckableSupplierEmails
                        label="P2P Output Table"
                        checked={p2pSupplier}
                        onChecked={handleP2pSupplierChange}
                        emails={p2pSupplierAddresses}
                        loading={loadingP2p}
                        disableIfEmpty
                        disableText
                        tooltipOpen={showErrorTooltipP2P}
                        tooltipOnClose={() => setShowErrorTooltipP2P(false)}
                        tooltipError={errorP2P}
                        disableCheckbox={isDisabled('sendingOptions.p2pSupplier')}
                    />
                    <CheckableSupplierEmails
                        label="Vendor Master Data"
                        checked={vmdSupplier}
                        onChecked={handleVmdSupplierChange}
                        emails={vmdSupplierAddresses}
                        loading={loadingVmd}
                        disableIfEmpty
                        disableText
                        tooltipOpen={showErrorTooltipVMD}
                        tooltipOnClose={() => setShowErrorTooltipVMD(false)}
                        tooltipError={errorVMD}
                        disableCheckbox={isDisabled('sendingOptions.vmdSupplier')}
                    />
                    <CheckableSupplierEmails
                        label="Preferred Supplier List"
                        checked={pslSupplier}
                        onChecked={handlePslSupplierChange}
                        emails={vendorContactEmail}
                        loading={false}
                        disableTooltip
                        disableIfEmpty
                        disableText
                        disableCheckbox={isDisabled('sendingOptions.pslSupplier')}
                    />
                </Box>
            </Box>}
            <Box sx={{display: 'flex', alignItems: 'center'}} gap={1}>
                <FormControlLabel
                    control=
                        {<Checkbox
                            checked={sendToOther}
                            onChange={(event) =>{
                                setSendToOther(event.target.checked);
                                if (event.target.checked) {
                                    setDefaultValuesAfterLoading(true);
                                }
                            }}
                        />}
                    label="Other (Nestle email addresses)"
                    sx={{whiteSpace: 'nowrap'}}
                    disabled={isDisabled('sendingOptions.sendToOther') || !userHasAccessToPOSendingOptions}
                />
                <UserFieldMultiSelect
                    context={props.context}
                    prTag={props.prTag}
                    disabled={isDisabled('sendingOptions.otherRecipients') || !userHasAccessToPOSendingOptions}
                />
            </Box>
            {sendToOther && <Box sx={{display: 'flex', alignItems: 'top'}} gap={5} className="pr-editor-sending-details-supplier-from-container">
                <p>Use emails from: </p>
                <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 2}}>
                    <CheckableSupplierEmails
                        label="P2P Output Table"
                        checked={p2pOther}
                        onChecked={handleP2pOtherChange}
                        emails={p2pOtherSupplierAddresses}
                        loading={loadingP2pOther}
                        disableIfEmpty
                        disableText
                        tooltipOpen={showErrorTooltipP2POther}
                        tooltipOnClose={() => setShowErrorTooltipP2POther(false)}
                        tooltipError={errorP2POther}
                        disableCheckbox={isDisabled('sendingOptions.p2pOther')}
                    />
                </Box>
            </Box>}
            <Box sx={{display: 'flex', alignItems: 'center'}} >
                <FormControlLabel
                    control={<Checkbox checked={hidePrice} onChange={(event) => setHidePrice(event.target.checked)} />}
                    label="Hide price"
                    disabled={hidePriceDisabled}
                />
            </Box>
            <div className={'pr-editor-sending-coversheet'}>
                <div>Coversheet:</div>
                <TextField
                    variant={'standard'}
                    value={coversheet}
                    disabled
                    InputProps={{
                        startAdornment: ( loadingCoversheet &&
                            <InputAdornment position="start">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ),
                    }}/>
            </div>
            <div className={'pr-editor-comments'}>
                <TextField
                    fullWidth={true}
                    id="outlined-multiline-external-comments"
                    label="Comments for supplier"
                    multiline
                    rows={4}
                    focused={true}
                    defaultValue={externalComment}
                    onBlur={(event) => setExternalComment(event.target.value)}
                    disabled={isDisabled('sendingOptions.supplierComment')}
                />
            </div>
        </Box>
    );
}

SendingOptionsDetails.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
};
